import { useCallback, useEffect, useMemo, useRef, useState } from 'react';

import { FilterComboBoxMenuItem, matchesSearch } from './filter-combo-box-menu-item';
import { FilterComboBoxProps, FilterComboBoxWrapperProps, HighlightedMenuItem } from './filter-combo-box.types';
import { addStyles, debounce, Popover } from '../..';
import { ComboBoxInput } from '../combo-box/combo-box-input';

const classes = {
  wrapper: 'inline-block relative',
  input: `bg-white rounded-t-lg py-2 px-3 border-none shadow-dark`,
  popover: {
    root: 'origin-top-right absolute z-150 rounded-t-none',
    base: 'flex-col max-h-60 overflow-y-auto rounded-t-none border-t border-t-gray-300 shadow-dark',
    clearSelectionButton:
      'py-2.5 px-5 border-b border-gray-300 text-primary hover:bg-gray-100 active:bg-gray-200 cursor-pointer select-none underline font-medium decoration-primary underline-offset-2 text-sm',
  },
};

export const getWidth = (element: HTMLDivElement | null) => {
  return element?.clientWidth;
};

// eslint-disable-next-line max-lines-per-function
export const FilterComboBoxWrapper = <T extends HighlightedMenuItem>(props: FilterComboBoxWrapperProps<T>) => {
  const {
    placeholder = 'Search',
    size,
    width,
    autoFocus,
    open,
    isDropdownEmpty,
    onDropdownVisibleChange,
    onSearch,
    onBlur: onBlurCallback,
    onFocus: onFocusCallback,
    defaultSearchTerm,
    children,
    popoverClassesBase,
    selectedItems = [],
    onDeselect,
    tagProps,
  } = props;
  const [isOpen, setIsOpen] = useState(open || false);
  const ref = useRef<HTMLDivElement>(null);
  const menuContentRef = useRef<HTMLDivElement>(null);

  const onFocus = (e: React.FocusEvent<HTMLInputElement>) => {
    setIsOpen(true);
    onDropdownVisibleChange?.(true);
    onFocusCallback?.(e);
  };

  useEffect(() => {
    if (open !== undefined) {
      setIsOpen(open);
    }
  }, [open]);

  const contentWidth = width ?? `${menuContentRef.current?.clientWidth}px`;

  return (
    <div ref={ref} className={classes.wrapper}>
      <Popover
        showArrow={false}
        trigger="external"
        open={isOpen}
        onOpenChange={setIsOpen}
        offsetOptions={{ mainAxis: 0 }}
        overlayClassName={classes.popover.root}
        overlayStyle={{ width: contentWidth }}
        innerContentClassName={popoverClassesBase ?? classes.popover.base}
        content={children}
        autoPlacementOptions={{ allowedPlacements: ['top', 'bottom'] }}
        isDismissBubbleEnabled={true}
      >
        <div ref={menuContentRef} style={{ width }}>
          <ComboBoxInput
            autoFocus={autoFocus}
            selectedItems={selectedItems}
            size={size}
            className={addStyles([classes.input, !isOpen || isDropdownEmpty ? 'rounded-b-lg' : ''])}
            style={{ width }}
            placeholder={placeholder}
            onSearch={debounce(onSearch, 300)}
            onDeselect={(item) => () => onDeselect?.(item)}
            onBlur={onBlurCallback}
            onFocus={onFocus}
            defaultValue={defaultSearchTerm}
            tagProps={{ baseType: 'table', ...tagProps }}
            containerSize={'medium'}
          />
        </div>
      </Popover>
    </div>
  );
};

export const useFilterComboboxSelection = <T extends HighlightedMenuItem>(
  defaultValue: T | T[],
  onChange: (selectedItems: T[], items: T[]) => void,
  data: T[]
) => {
  const [selectedItems, setSelectedItems] = useState<T[]>(Array.isArray(defaultValue) ? defaultValue : [defaultValue]);

  const onToggleItem = useCallback(
    (selectedItem: T, isSelected: boolean) => {
      const newSelection = isSelected
        ? [...selectedItems, selectedItem]
        : selectedItems.filter((selectedValue) => selectedValue.value !== selectedItem.value);
      onChange(newSelection, data);
      setSelectedItems(newSelection);
    },
    [data, onChange, selectedItems]
  );

  const deselectAll = useCallback(
    (e: React.MouseEvent<HTMLElement>) => {
      onChange([], data);
      setSelectedItems([]);
      e.stopPropagation();
    },
    [data, onChange]
  );

  return { selectedItems, setSelectedItems, onToggleItem, deselectAll };
};

// eslint-disable-next-line max-lines-per-function
export const FilterComboBox = <T extends HighlightedMenuItem>(props: FilterComboBoxProps<T>) => {
  const {
    data,
    onChange,
    open,
    autoFilterItems = false,
    onSearch: onSearchCallback,
    renderListItem,
    defaultValue = [],
    defaultSearchTerm,
    ...rest
  } = props;
  const { selectedItems, onToggleItem, deselectAll } = useFilterComboboxSelection(defaultValue, onChange, data);
  const [searchTerm, setSearchTerm] = useState<string>(defaultSearchTerm ?? '');

  const isMatch = useCallback(
    (item: T) => !autoFilterItems || matchesSearch(item, searchTerm),
    [autoFilterItems, searchTerm]
  );

  const onSearch = (searchString: string) => {
    setSearchTerm(searchString);
    onSearchCallback(searchString);
  };

  const menuItemRenderer = useCallback(
    (value: T, selected = false) => (
      <FilterComboBoxMenuItem
        key={value.value}
        item={{ ...value, textToHighlight: searchTerm }}
        defaultSelection={selected}
        onSelect={onToggleItem}
        className="text-sm"
        renderItem={renderListItem}
      />
    ),
    [onToggleItem, renderListItem, searchTerm]
  );
  const itemsNotSelected = useMemo(
    () =>
      data
        .filter(
          (dataItem) =>
            !selectedItems.some((selectedItem) => selectedItem.value === dataItem.value) && isMatch(dataItem)
        )
        .map((element) => menuItemRenderer(element, false)),
    [data, isMatch, menuItemRenderer, selectedItems]
  );
  const itemsSelected = useMemo(
    () =>
      selectedItems
        .filter((selectedItem) => matchesSearch(selectedItem, searchTerm))
        .map((element) => menuItemRenderer(element, true)),
    [menuItemRenderer, searchTerm, selectedItems]
  );
  const isDropdownEmpty = useMemo(
    () => !(data.some((i) => isMatch(i)) || selectedItems.length > 0),
    [data, isMatch, selectedItems]
  );

  return (
    <FilterComboBoxWrapper<T>
      open={open}
      isDropdownEmpty={isDropdownEmpty}
      onSearch={onSearch}
      onDeselect={(item) => onToggleItem(item, false)}
      {...rest}
    >
      {itemsSelected}
      {selectedItems.length > 0 && (
        <div
          role="button"
          aria-label="Clear selection"
          onClick={deselectAll}
          className={classes.popover.clearSelectionButton}
        >
          Clear selected
        </div>
      )}
      {itemsNotSelected}
    </FilterComboBoxWrapper>
  );
};
export default FilterComboBox;
